import React, { useMemo, useState } from 'react';
import { Button as RSButton } from 'src/components/ui';
import Icon from 'src/components/Icon';
import { useJnx } from '../../util/jnx';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import { useOpenModal } from '../FormModal';
import Loader from '../Loader';
import Notification from '../Notification';
import getPathFromId from '../../util/getPathFromId';
import useTestId from '../../hooks/useTestId';

function Button(props) {
  const {
    registry,
    schema: { title, icon, tooltip },
    fieldPathId: { $id } = {},
    uiSchema: {
      'ui:titleExpr': titleExpr,
      'ui:color': color,
      classNames: className,
      'ui:loaderOnClick': loaderOnClick,
      'ui:loaderText': loaderText,
      'ui:successMessage': successMessage,
      'ui:errorMessage': errorMessage,
      'ui:onClick': onClickExpr,
      'ui:simulateLabel': simulateLabel,
    } = {},
  } = props;
  const formContext = registry?.formContext;

  const {
    setFormDataValues,
    sideChannel,
    bindings: fcBindings,
    // openModal,
  } = formContext;

  const openModal = useOpenModal();
  const [loading, setLoading] = useState();
  const [toastState, setToastState] = useState({ type: null, message: null });
  const testIdProps = useTestId($id);

  const path = useMemo(() => getPathFromId($id), [$id]);
  const [rootFormData, formObject] = useSideChannelSubscription(sideChannel, [0, 1]) || [{}];
  const titleJnx = useJnx(titleExpr);
  const bindings = useMemo(
    () => ({
      ...fcBindings,
      openModal,
      rootFormData,
      formObject,
      formContext,
    }),
    [openModal, rootFormData, formObject, fcBindings, formContext]
  );

  const text = useMemo(() => (title || titleJnx?.eval(rootFormData, path, bindings))?.trim(), [title, titleJnx, path, rootFormData, bindings]);

  const functionBinds = useMemo(
    () => ({
      set: setFormDataValues,
      log: (...args) => console.log('jsonata console log\n', ...args),
    }),
    [setFormDataValues]
  );

  const onClick = useJnx(
    onClickExpr,
    {
      fn: async ([e], onClickJnx) => {
        e.preventDefault();

        if (onClickJnx) {
          if (loaderOnClick) setLoading(true);
          setToastState({ type: null, message: null });

          try {
            await onClickJnx.evalAsync(rootFormData, path, bindings);
            if (successMessage) {
              setToastState({ type: 'success', message: successMessage });
            }
          } catch (error) {
            const message = error?.message || error?.toString?.() || 'An error occurred.';
            setToastState({ type: 'danger', message: errorMessage || message });
          } finally {
            if (loaderOnClick) setLoading(false);
          }
        }
      },
      functionBinds,
    },
    [openModal, path, rootFormData, bindings, loaderOnClick, functionBinds]
  );

  return (
    <>
      {simulateLabel && <label>&nbsp;</label>}
      <RSButton
        className={[className, simulateLabel && 'disblock'].filter(Boolean).join(' ')}
        color={color}
        type="button"
        onClick={onClick}
        title={tooltip || undefined}
        {...testIdProps}
      >
        {loading ? (
          <Loader sm spinner inline>
            {loaderText}
          </Loader>
        ) : (
          <>
            {icon ? <Icon icon={icon} /> : null}
            {icon && text ? ' ' : null}
            {text || null}
          </>
        )}
      </RSButton>
      {toastState.message ? (
        <Notification color={toastState.type === 'success' ? 'success' : 'danger'} className="mt-2">
          {toastState.message}
        </Notification>
      ) : null}
    </>
  );
}

export default Button;
